import { useMutation, useQuery } from "@tanstack/react-query";
import {
  getJobDefinitions,
  getQueryJobData,
  getQueryJobExecutions,
  getQueryJobFilters,
  updateQueryJobName,
} from "../../helpers/apis";
import classNames from "classnames";
import styles from "./styles.module.scss";
import { DataTable } from "../../components/tables";
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { parseSortDirection } from "@/components/tables/types";
import { RelativeComponentLoader } from "../../components/loader";
import { default as ArrowLeftIcon } from "../../assets/icons/arrow_left.svg?react";
import { default as EditIcon } from "@/assets/icons/edit.svg?react";
import { Input } from "@altimateai/lego";
import { Button } from "@altimateai/lego";
import dayjs from "dayjs";
import { Paginate } from "../../components/paginate";
import { Sheet, SheetContent } from "@altimateai/lego";
import arrow_down from "../../assets/icons/arrow_down.svg";
import { CueCodeEditor } from "@/components/codeEditor";
import { roundToTwoDecimalPlaces } from "../../helpers/utils";
import {
  GET_JOB_DEFINTIONS,
  GET_JOB_EXECUTIONS,
  GET_JOB_FILTERS,
  GET_QUERY_JOB_DATA,
} from "./constants";
import { FilterTagWrapper } from "../../components/tags";
import { ScrollContainer } from "../../components/scrollContainer";
import { useNavigate } from "react-router-dom";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { CustomTooltip } from "../../components/graph/misc";
import { useAppState } from "@/modules/app/useAppContext";
import { formatNumber } from "@/uiCore";
import { Stack } from "@altimateai/lego";
import useFiltersBySearchParams from "@/helpers/useFiltersBySearchParams";
import { DownloadTable } from "@/components/downloadTable";
import { downloadAllQueryJobExecutionsCSV } from "@/helpers/apis";
import {
  QueryJobItem,
  QueryJobExecutionItem,
  QueryJobExecutionsFilters,
  QueryJobDefinitionItem,
  UpdateQueryJobNameRequest,
  JobFilters,
  QueryGraphDataItem,
} from "@/pages/query/types";
import { SortDirection, TableHeaderType, FilterType } from "@/components/tables/types";

interface JobSelectedHeaderProps {
  initialJobName: string;
  jobData: QueryJobItem;
  close: () => void;
  environment?: string;
}

interface JobInsightsProps {
  jobData: QueryJobItem;
}

interface JobQueriesProps {
  selectedJob: QueryJobItem;
}

interface QueryDropdownProps {
  item: QueryJobDefinitionItem;
  index: number;
  selectedQuery: number | null;
  setSelectedQuery: (index: number | null) => void;
}

interface ChartTickProps {
  x?: number;
  y?: number;
  payload?: { value: string | number };
}

interface JobCostAndExecutionTimeGraphProps {
  jobId: string | undefined;
}

interface JobExecutionsProps {
  selectedJob: QueryJobItem;
  jobFilters: JobFilters;
  autoTune?: boolean;
}

const SORT_KEY_MAP: Record<string, keyof typeof INITIAL_EXECUTION_SORT_ORDER> = {
  timestamp: "timestamp",
  cost: "cost",
  job_execution_time: "job_execution_time",
  total_data_scanned: "total_data_scanned",
  total_local_spill: "total_local_spill",
  total_remote_spill: "total_remote_spill",
};

const INITIAL_EXECUTION_SORT_ORDER = {
  timestamp: "desc" as SortDirection,
  cost: "" as SortDirection,
  job_execution_time: "" as SortDirection,
  total_data_scanned: "" as SortDirection,
  total_local_spill: "" as SortDirection,
  total_remote_spill: "" as SortDirection,
};

const JobSelectedHeader = ({
  initialJobName,
  jobData,
  close,
  environment,
}: JobSelectedHeaderProps) => {
  const { currency } = useAppState();
  const navigate = useNavigate();
  const [isNameEditing, setIsNameEditing] = useState(false);
  const [jobName, setJobName] = useState(initialJobName);
  const [showJobInsights, setShowJobInsights] = useState(false);

  const toggleInsights = () => {
    setShowJobInsights(prev => !prev);
  };

  const jobInfo = useMemo(() => {
    const jobFrequency = jobData?.job_frequency;
    const jobInfoList = [
      {
        label: "Average run time",
        value: jobData?.avg_total_execution_time,
      },
      {
        label: "90th percentile",
        value: jobData?.p90_total_execution_time,
      },
      {
        label: "Max run time",
        value: jobData?.max_total_execution_time,
      },
      {
        label: "Job Frequency",
        value: jobFrequency,
      },
      {
        label: "Annualized Cost",
        value: formatNumber(jobData?.annualized_cost ?? "", { currency }),
      },
    ];

    if (environment) {
      jobInfoList.push({
        label: "Environment",
        value: environment,
      });
    }

    return jobInfoList;
  }, [jobData, environment, currency]);

  const { mutate } = useMutation({
    mutationFn: (data: UpdateQueryJobNameRequest) => updateQueryJobName(jobData.job_id, data),
    onSuccess: data => {
      if (data?.ok) {
        setIsNameEditing(false);
      }
    },
  });

  return (
    <Stack align="start" gap="sm" className="bg-blue-light p-3 border-radius-top">
      <button
        className="p-1 br-2 bg-blue text-white cursor-pointer"
        onClick={e => {
          e.stopPropagation();
          close();
          if (environment) {
            navigate("/query/autotune_jobs");
          } else {
            navigate("/query/jobs");
          }
        }}
      >
        <ArrowLeftIcon className="icon-sm" />
      </button>
      <Stack direction="column" gap="sm">
        <Stack gap="xs" align="center">
          {!isNameEditing ? (
            <>
              <div className="text-primary">{jobName}</div>
              <EditIcon
                className={classNames("cursor-pointer", styles.edit_icon, {
                  [styles.active]: isNameEditing,
                })}
                onClick={e => {
                  e.stopPropagation();
                  if (isNameEditing) {
                    setIsNameEditing(false);
                  } else {
                    setIsNameEditing(true);
                  }
                }}
              />
            </>
          ) : (
            <>
              <Input
                disabled={!isNameEditing}
                className={styles.name_input}
                value={jobName}
                onChange={e => setJobName(e.target.value)}
                placeholder={initialJobName}
              />
              <Button
                onClick={e => {
                  e.stopPropagation();
                  mutate({ job_name: jobName });
                }}
              >
                Save
              </Button>
              <Button
                variant="outline"
                onClick={e => {
                  e.stopPropagation();
                  setIsNameEditing(false);
                }}
              >
                Cancel
              </Button>
            </>
          )}
        </Stack>
        <Stack gap="xl">
          {jobInfo.map(item => (
            <div key={item.label}>
              <span className="text-muted">{item.label}: </span>
              <span>{item.value}</span>
            </div>
          ))}
        </Stack>
      </Stack>
      <div className="spacer" />
      <Button variant="outline" onClick={toggleInsights}>
        Job Insights
      </Button>
      <Sheet open={showJobInsights} onOpenChange={toggleInsights}>
        <SheetContent side="right" className="!al-w-[660px]" aria-label="Job insights modal">
          <JobInsights jobData={jobData} />
        </SheetContent>
      </Sheet>
    </Stack>
  );
};

const JobInsights = ({ jobData }: JobInsightsProps) => {
  const { currency } = useAppState();
  const infoData = [
    {
      label: "Annualized Cost",
      value: formatNumber(jobData.annualized_cost ?? "", { currency }),
    },
    {
      label: "Average Execution Time",
      value: jobData.avg_total_execution_time,
    },
  ];
  return (
    <div className={styles.job_insights}>
      <Stack gap="sm" align="center">
        <div className={styles.title}>Job Name: {jobData.job_name}</div>
      </Stack>
      <div className={styles.info}>
        {infoData.map((item, index) => (
          <div key={index}>
            <div className={styles.info_title}>
              {item.label}: <span className={styles.info_text}>{item.value}</span>
            </div>
          </div>
        ))}
      </div>
      <JobQueries selectedJob={jobData} />
    </div>
  );
};

const JobQueries = ({ selectedJob }: JobQueriesProps) => {
  const [selectedQuery, setSelectedQuery] = useState<number | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: [GET_JOB_DEFINTIONS, selectedJob.job_id],
    queryFn: () => getJobDefinitions(selectedJob.job_id),
  });

  const headers = ["Query Text", "Avg. Execution Time", "Average Cost"];

  if (isLoading) return <RelativeComponentLoader />;

  return (
    <div className={styles.queries}>
      <div className={styles.title}>Queries</div>
      <div className={styles.table}>
        <div className={styles.header}>
          {headers.map((header, index) => (
            <>
              {index !== 0 && <div className={styles.spacer} />}
              <div className={styles.text} key={index}>
                {header}
              </div>
            </>
          ))}
        </div>
        <ScrollContainer>
          <div className={styles.scrollContent}>
            {data?.map((item, index) => (
              <QueryDropdown
                key={index}
                item={item}
                index={index}
                selectedQuery={selectedQuery}
                setSelectedQuery={setSelectedQuery}
              />
            ))}
          </div>
        </ScrollContainer>
      </div>
    </div>
  );
};

const QueryDropdown = ({ item, index, selectedQuery, setSelectedQuery }: QueryDropdownProps) => {
  const { currency } = useAppState();
  const navigate = useNavigate();
  const closeDropdown = () => {
    if (selectedQuery !== index) setSelectedQuery(index);
    else setSelectedQuery(null);
  };

  const handleQueryClick = () => {
    navigate(`/query/all?queryParameterizedHash=${item.query_parameterized_hash}`);
  };

  const infoData = [
    {
      label: "Cost",
      value: formatNumber(item.avg_cost, { currency }),
    },
    {
      label: "p90 exc time",
      value: item.p90_execution_time,
    },
  ];

  return (
    <>
      <div
        className={styles.query_dropdown}
        role="button"
        tabIndex={0}
        onClick={closeDropdown}
        onKeyDown={e => {
          if (e.key === "Enter" || e.key === " ") closeDropdown();
        }}
      >
        <div>{item?.query_name || item?.query_trimmed}</div>
        <div>{item?.avg_execution_time}</div>
        <div></div>
        <div className={styles.light_text}>{formatNumber(item.avg_cost, { currency })}</div>
        <img
          src={arrow_down}
          className={classNames(styles.image, {
            [styles.rotate]: selectedQuery === index,
          })}
          alt="arrow"
        />
      </div>
      {selectedQuery === index && (
        <>
          <div className={styles.query_information}>
            <div className={styles.query_info}>
              {infoData.map((infoItem, infoIndex) => (
                <div key={infoIndex}>
                  <div className={styles.info_title}>{infoItem.label}: </div>
                  <span className={styles.info_text}>{infoItem.value}</span>
                </div>
              ))}
            </div>
            <Button onClick={handleQueryClick}>View Query</Button>
          </div>
          <CueCodeEditor value={item?.query_text} height="20vh" />
        </>
      )}
    </>
  );
};

const CustomXAxisTick = ({ x, y, payload }: ChartTickProps) => {
  return (
    <g transform={`translate(${x},${y})`}>
      <text x={0} y={0} dx={-30} dy={4} transform="rotate(270)" textAnchor="middle" fill="#66768D">
        {dayjs(String(payload?.value ?? "")).format("MMM DD")}
      </text>
    </g>
  );
};

const CustomYAxisTickCost = ({ x, y, payload }: ChartTickProps) => {
  const { currencySymbol } = useAppState();
  return (
    <g transform={`translate(${x},${y})`}>
      <text textAnchor="end" dy={5} fill="#66768D">
        {currencySymbol}
        {payload?.value}
      </text>
    </g>
  );
};

const CustomYAxisTickTime = ({ x, y, payload }: ChartTickProps) => (
  <g transform={`translate(${x},${y})`}>
    <text textAnchor="start" dy={5} fill="#66768D">
      {payload?.value} mins
    </text>
  </g>
);

const JobCostAndExecutionTimeGraph = ({ jobId }: JobCostAndExecutionTimeGraphProps) => {
  const { currency } = useAppState();
  const [graphData, setGraphData] = useState<QueryGraphDataItem[] | null>(null);
  const [typeFilter, setTypeFilter] = useState("all");

  const { data: _data, isLoading } = useQuery({
    queryKey: [GET_QUERY_JOB_DATA],
    queryFn: () => {
      return getQueryJobData(jobId ?? "", { period: 30 });
    },
  });

  // Handle successful data fetch (replaces onSuccess callback removed in TanStack Query v5)
  useEffect(() => {
    if (_data) {
      setGraphData(_data?.data);
    }
  }, [_data]);

  const handleLegendClick = (legendData: { value?: string }) => {
    const datakey = legendData?.value;
    if (datakey) {
      if (typeFilter === datakey) {
        setTypeFilter("all");
      } else {
        setTypeFilter(datakey);
      }

      return;
    }
    setTypeFilter("all");
  };

  const stackedData = useMemo(() => {
    if (!graphData || graphData.length === 0) return [];
    return graphData.map(entry => ({
      time: entry.date,
      cost: roundToTwoDecimalPlaces(entry.cost),
      execution_time: roundToTwoDecimalPlaces(entry.execution_time),
      costUI: formatNumber(entry.cost, { currency }),
      executionTimeUI: `${roundToTwoDecimalPlaces(entry.execution_time)} mins`,
    }));
  }, [currency, graphData]);

  if (stackedData && stackedData.length === 0) return null;

  if (isLoading) return <RelativeComponentLoader componentHeight={30} />;

  return (
    <div className={styles.job_cost_execution_graph}>
      <div className={styles.title}>Job Cost and Execution Time for last 30 days</div>
      <ResponsiveContainer minHeight={400} className={styles.graph}>
        <LineChart
          data={stackedData}
          margin={{
            top: 50,
            right: 50,
            left: 50,
            bottom: 50,
          }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="time" tick={<CustomXAxisTick />} />
          <YAxis
            yAxisId="left"
            stroke="#9382D9"
            label={{
              value: "Cost",
              fill: "black",
              angle: 0,
              position: "insideTopLeft",
              offset: -20,
            }}
            tick={<CustomYAxisTickCost />}
          />
          <YAxis
            yAxisId="right"
            orientation="right"
            stroke="#01CD8C"
            label={{
              value: "Time",
              fill: "black",
              angle: 0,
              position: "insideTopRight",
              offset: -20,
            }}
            tick={<CustomYAxisTickTime />}
          />
          <Tooltip
            content={
              <CustomTooltip
                nameValue={[
                  {
                    name: "Cost",
                    value: "costUI",
                    color: "#9382D9",
                  },
                  {
                    name: "Execution Time",
                    value: "executionTimeUI",
                    color: "#01CD8C",
                  },
                ]}
              />
            }
          />
          <Line
            name="Cost"
            yAxisId="left"
            type="monotone"
            dataKey={`${typeFilter == "all" || typeFilter == "Cost" ? "cost" : "cost_hidden"}`}
            stroke="#9382D9"
            dot={{ strokeWidth: 2, fill: "#9382D9" }}
          />
          <Line
            name="Execution Time"
            yAxisId="right"
            type="monotone"
            dataKey={`${
              typeFilter == "all" || typeFilter == "Execution Time"
                ? "execution_time"
                : "execution_time_hidden"
            }`}
            stroke="#01CD8C"
            dot={{ strokeWidth: 2, fill: "#247EFE" }}
          />
          <Legend
            verticalAlign="bottom"
            wrapperStyle={{ bottom: 4 }}
            onClick={handleLegendClick}
            formatter={value => <span className={styles.legend}>{value}</span>}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

const JobExecutions = ({ selectedJob, jobFilters }: JobExecutionsProps) => {
  const { currency, currencySymbol } = useAppState();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { deleteSearchParamsByKeyValue, updateSearchParamsByKey } = useFiltersBySearchParams();
  const [page, setPage] = useState(0);
  const sortAttribute = searchParams.get("sort_key") || "timestamp";
  const sortOrder = parseSortDirection(searchParams.get("sort_order"), "desc");
  const warehouseFilter = jobFilters?.warehouseFilter || [];
  const warehouseSizeFilter = jobFilters?.warehouseSizeFilter || [];
  const userFilter = jobFilters?.userFilter || [];
  const costFilter = jobFilters?.costFilter || 0;

  const getFilterParams = (): QueryJobExecutionsFilters => {
    const params: QueryJobExecutionsFilters = { page: page + 1, size: 8 };
    if (sortAttribute && sortAttribute in SORT_KEY_MAP) {
      params.sort_key = sortAttribute;
      params.sort_order = sortOrder;
    }
    if (costFilter) params.executionCostFilter = String(costFilter);
    if (warehouseFilter.length > 0) params.warehouses = warehouseFilter;
    if (warehouseSizeFilter.length > 0) params.warehouse_sizes = warehouseSizeFilter;
    if (userFilter.length > 0) params.users = userFilter;
    return params;
  };

  const getJobFilterParams = () => {
    const filters: { job_id?: string } = {};
    if (selectedJob?.job_id) filters.job_id = selectedJob.job_id;
    return filters;
  };

  const { data: filters, isLoading: isfiltersLoading } = useQuery({
    queryKey: [GET_JOB_FILTERS, selectedJob.job_id],
    queryFn: () => getQueryJobFilters(getJobFilterParams()),
  });

  const { data, isLoading } = useQuery({
    queryKey: [
      GET_JOB_EXECUTIONS,
      page,
      ...warehouseFilter,
      ...warehouseSizeFilter,
      ...userFilter,
      sortOrder,
      sortAttribute,
      selectedJob,
      costFilter,
    ],
    queryFn: () => getQueryJobExecutions(selectedJob.job_id, getFilterParams()),
  });

  if (isLoading || isfiltersLoading) return <RelativeComponentLoader />;

  const handleExecutionClick = (item: QueryJobExecutionItem) => {
    navigate(`/query/all?sessionID=${item.session_id}`);
  };

  const handleCostFilterChange = (value: string) => {
    updateSearchParamsByKey({ cost: value });
  };

  const handleWarehouseFilterChange = (value: string[]) => {
    updateSearchParamsByKey({ warehouses: value });
  };

  const handleUserFilterChange = (value: string[]) => {
    updateSearchParamsByKey({ users: value });
  };

  const handleWarehouseSizeFilterChange = (value: string[]) => {
    updateSearchParamsByKey({ warehouseSize: value });
  };

  const handleDownloadClick = async (): Promise<void> => {
    const params = getFilterParams();
    await downloadAllQueryJobExecutionsCSV(selectedJob?.job_id, params);
  };

  const warehouseOptions = (filters?.warehouses ?? [])
    .filter(Boolean)
    .map(w =>
      w === "" ? { label: "None", value: w as string } : { label: w as string, value: w as string }
    );

  const warehouseSizeOptions = (filters?.warehouse_sizes ?? [])
    .filter(Boolean)
    .map(env => ({ label: env as string, value: env as string }));

  const userOptions = (filters?.users ?? [])
    .filter(Boolean)
    .map(w => ({ label: w as string, value: w as string }));

  const listingTableHeaders: TableHeaderType<QueryJobExecutionItem>[] = [
    {
      id: 1,
      label: "Job Start Time",
      uid: "start_time",
      width: "17%",
      sort: {
        sortSearchKey: "sort_key",
        sortOrderSearchKey: "sort_order",
        sortParamValue: "timestamp",
      },
      render: (item: QueryJobExecutionItem) => (
        <Stack align="start" direction="column">
          <div>{dayjs(item.start_time).format("DD-MMM-YY HH:mm A")}</div>
        </Stack>
      ),
    },
    {
      id: 3,
      label: "Est. Cost",
      uid: "cost",
      width: "11%",
      sort: {
        sortSearchKey: "sort_key",
        sortOrderSearchKey: "sort_order",
      },
      filter: {
        filterType: FilterType.TEXT,
        value: String(costFilter) || undefined,
        placeHolder: `Specify cost in ${currencySymbol}`,
        onChange: handleCostFilterChange,
        label: "Annualized Cost",
        searchKey: "cost",
      },
      render: (item: QueryJobExecutionItem) => <div>{formatNumber(item?.cost, { currency })}</div>,
    },
    {
      id: 4,
      label: "Exec. Time",
      uid: "job_execution_time",
      width: "11%",
      sort: {
        sortSearchKey: "sort_key",
        sortOrderSearchKey: "sort_order",
      },
      render: (item: QueryJobExecutionItem) => <div>{item?.job_execution_time}</div>,
    },
    {
      id: 5,
      label: "Warehouse",
      uid: "warehouse_name",
      width: "17%",
      filter: {
        filterType: FilterType.DROPDOWN,
        value: warehouseFilter,
        searchKey: "warehouses",
        options: warehouseOptions,
        onChange: handleWarehouseFilterChange,
      },
      render: (item: QueryJobExecutionItem) => <div>{item?.warehouse_name}</div>,
    },
    {
      id: 6,
      label: "Warehouse Size",
      uid: "warehouse_size",
      width: "11%",
      filter: {
        filterType: FilterType.DROPDOWN,
        value: warehouseSizeFilter,
        searchKey: "warehouseSize",
        options: warehouseSizeOptions,
        onChange: handleWarehouseSizeFilterChange,
      },
      render: (item: QueryJobExecutionItem) => <div>{item?.warehouse_size}</div>,
    },
    {
      id: 7,
      label: "User",
      uid: "user_name",
      width: "17%",
      filter: {
        filterType: FilterType.DROPDOWN,
        value: userFilter,
        searchKey: "users",
        options: userOptions,
        onChange: handleUserFilterChange,
      },
      render: (item: QueryJobExecutionItem) => <div>{item?.user_name}</div>,
    },
    {
      id: 8,
      label: "GB Scanned",
      uid: "total_data_scanned",
      width: "8%",
      sort: {
        sortSearchKey: "sort_key",
        sortOrderSearchKey: "sort_order",
      },
      render: (item: QueryJobExecutionItem) => <div>{item?.total_data_scanned}</div>,
    },
    {
      id: 9,
      label: "Local Spillage",
      uid: "total_local_spill",
      width: "8%",
      sort: {
        sortSearchKey: "sort_key",
        sortOrderSearchKey: "sort_order",
      },
      render: (item: QueryJobExecutionItem) => <div>{item?.total_local_spill}</div>,
    },
    {
      id: 10,
      label: "Remote Spillage",
      uid: "total_remote_spill",
      width: "8%",
      sort: {
        sortSearchKey: "sort_key",
        sortOrderSearchKey: "sort_order",
      },
      render: (item: QueryJobExecutionItem) => <div>{item?.total_remote_spill}</div>,
    },
  ];

  return (
    <div className={classNames(styles.jobs, styles.card_mode)}>
      <Stack className="al-mt-2 al-mb-2" align="center">
        <div className={styles.title}>Query Job Executions</div>
        <FilterTagWrapper
          filters={{
            costFilter: {
              label: "Cost",
              filterStr: String(costFilter),
              onclose: () => {
                deleteSearchParamsByKeyValue({ cost: undefined });
              },
            },
            userFilter: {
              label: "Users",
              filterStr: userFilter,
              onclose: (removedFilter?: string) => {
                deleteSearchParamsByKeyValue({ users: removedFilter });
              },
            },
            warehouseFilter: {
              label: "Warehouse",
              filterStr: warehouseFilter,
              onclose: (removedFilter?: string) => {
                deleteSearchParamsByKeyValue({ warehouses: removedFilter });
              },
            },
            warehouseSizeFilter: {
              label: "Warehouse Size",
              filterStr: warehouseSizeFilter,
              onclose: (removedFilter?: string) => {
                deleteSearchParamsByKeyValue({ warehouseSize: removedFilter });
              },
            },
          }}
        />
        <div className="spacer" />
        <DownloadTable handleDownload={handleDownloadClick} />
      </Stack>
      <DataTable
        header={listingTableHeaders}
        onRowClick={(item: QueryJobExecutionItem) => {
          handleExecutionClick(item);
        }}
        idKey={(item: QueryJobExecutionItem) => String(item.session_id)}
        data={{ items: data?.items ?? [] }}
      />
      <Paginate
        itemCount={data?.total ?? 0}
        page={page}
        pageSize={data?.size ?? 8}
        numPages={data?.pages ?? 0}
        onPageClick={setPage}
        onPageSizeChange={() => {}}
      />
    </div>
  );
};

export { JobSelectedHeader, JobExecutions, JobCostAndExecutionTimeGraph };
